/**
 * When the confirmation card becomes visible.
 *
 * A preview reaches the frontend on the `propose_*` TOOL RESULT, which is
 * several seconds before the agent finishes the sentence explaining it. Showing
 * the card the instant it arrives put a Confirm button on screen ahead of any
 * description of what confirming would do — the user's first sight of a 200-row
 * edit was a button, and the explanation scrolled in above it afterwards.
 *
 * So the card waits for one of two things:
 *
 *   1. The agent has SAID something since the preview arrived. This is the
 *      normal path: `propose_bulk_edit`'s next_step tells the agent to show the
 *      count and the before → after and ask, so a message essentially always
 *      follows.
 *   2. The turn has SETTLED. The fallback, and the reason this can't deadlock:
 *      if the agent proposes and then says nothing at all (it stops, errors, or
 *      the user interrupts), the card still appears rather than being stranded
 *      behind a message that will never come.
 *
 * Pair this with `pin: 'end'` in feedOrder — the gate decides WHETHER the card
 * shows, the pin decides WHERE. Both are needed: without the pin the card would
 * appear and then sort itself back above the very message that released it.
 */

/** Statuses that mean the agent is mid-turn and more output is expected. */
const BUSY_STATUSES = new Set(['running', 'initializing', 'rescheduling', 'creating']);

/**
 * Is the agent between turns?
 *
 * `pendingToolCalls` is load-bearing: Anthropic reports the same session status
 * for "paused for a tool roundtrip" and "turn over, your move", so the explicit
 * request − result counter is the only thing that separates them.
 */
export function isTurnSettled(status, pendingToolCalls = 0) {
  if ((pendingToolCalls || 0) > 0) return false;
  return !BUSY_STATUSES.has(status);
}

/**
 * The same rule for any card that arrived at `since` — a proposal or a
 * GroundTruth choice card: wait until the agent has said something since, or
 * its turn has settled.
 *
 * @param {object} session the agent session — { messages, status, pendingToolCalls }
 * @param {number|null} since when the card arrived; unstamped means no wait
 */
export function itemReadyToShow(session, since) {
  if (!Number.isFinite(since)) return true;
  const spokeSince = (session?.messages || []).some(
    (m) => m?.role === 'agent' && Number.isFinite(m.ts) && m.ts >= since,
  );
  if (spokeSince) return true;
  return isTurnSettled(session?.status, session?.pendingToolCalls);
}

/**
 * @param {object} session the agent session — { activePreview, messages, status, pendingToolCalls }
 * @returns {boolean} whether the confirmation card should render yet
 */
export function previewReadyToShow(session) {
  const preview = session?.activePreview;
  if (!preview) return false;
  // An unstamped preview has no "since" to compare against, so there is nothing
  // to wait for.
  return itemReadyToShow(session, Number.isFinite(preview.ts) ? preview.ts : null);
}

export default { previewReadyToShow, itemReadyToShow, isTurnSettled };
